/**
 * Per-user, per-explorer state persisted to localStorage.
 *
 * Saves: sort field+direction, active filters, manually-resized column widths.
 * Bucketed by `em3:explorer:<entityName>:<userId>` so each user's tuning sticks
 * across sessions and never bleeds between accounts. Follows the same pattern
 * as `mapViewportPersistence.ts` (validated reads, try/catch around localStorage
 * for private-mode/quota cases).
 *
 * `v` field allows future schema evolution — loaders ignore unknown shapes.
 */

import { clampWindowToViewport, MAP_TOP_OFFSET } from './clampWindowToViewport';

const STORAGE_PREFIX = 'em3:explorer';
const ANONYMOUS_BUCKET = 'anonymous';
const SCHEMA_VERSION = 1;

export interface SavedExplorerState {
  v: number;
  sort?: { field: string; direction: 'asc' | 'desc' } | null;
  filters?: Record<string, any>;
  widths?: Record<string, number>;  // keyed by column field name (NOT HOT col index)
  position?: { x: number; y: number };
  size?: { width: number; height: number };
  pageSize?: number;
}

export const explorerStorageKey = (
  entityName: string,
  userId: string | null | undefined,
): string => `${STORAGE_PREFIX}:${entityName}:${userId || ANONYMOUS_BUCKET}`;

const isPlainObject = (v: unknown): v is Record<string, any> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

const isValidState = (v: unknown): v is SavedExplorerState => {
  if (!isPlainObject(v)) return false;
  if (v.v !== SCHEMA_VERSION) return false;
  if (v.sort != null) {
    if (!isPlainObject(v.sort)) return false;
    if (typeof v.sort.field !== 'string') return false;
    if (v.sort.direction !== 'asc' && v.sort.direction !== 'desc') return false;
  }
  if (v.filters != null && !isPlainObject(v.filters)) return false;
  if (v.widths != null) {
    if (!isPlainObject(v.widths)) return false;
    for (const w of Object.values(v.widths)) {
      if (typeof w !== 'number' || !Number.isFinite(w) || w <= 0) return false;
    }
  }
  if (v.position != null) {
    if (!isPlainObject(v.position)) return false;
    if (typeof v.position.x !== 'number' || !Number.isFinite(v.position.x)) return false;
    if (typeof v.position.y !== 'number' || !Number.isFinite(v.position.y)) return false;
  }
  if (v.size != null) {
    if (!isPlainObject(v.size)) return false;
    if (typeof v.size.width !== 'number' || !Number.isFinite(v.size.width) || v.size.width <= 0) return false;
    if (typeof v.size.height !== 'number' || !Number.isFinite(v.size.height) || v.size.height <= 0) return false;
  }
  if (v.pageSize != null) {
    if (typeof v.pageSize !== 'number' || !Number.isInteger(v.pageSize) || v.pageSize <= 0) return false;
  }
  return true;
};

/**
 * Clamp a saved {position, size} into the current viewport so a window
 * placed (or shrunk) off-screen on a previous session is brought back into
 * the visible DOM. Leaves ≥ 100px of the window visible on every edge.
 */
export const clampToViewport = (
  position: { x: number; y: number } | undefined,
  size: { width: number; height: number } | undefined,
): { position?: { x: number; y: number }; size?: { width: number; height: number } } => {
  if (typeof window === 'undefined') return { position, size };
  // A saved window must fit the same way a new one does: keeping only 100px
  // on screen reopened the AFF Explorer past the right edge (D6).
  if (position && size) {
    return clampWindowToViewport(
      position,
      size,
      { width: window.innerWidth, height: window.innerHeight },
      20,
      { top: MAP_TOP_OFFSET },
    );
  }
  const vw = window.innerWidth || 1024;
  const vh = window.innerHeight || 768;
  const MIN_VIS = 100;
  const out: { position?: { x: number; y: number }; size?: { width: number; height: number } } = {};
  if (size) {
    out.size = {
      width: Math.min(size.width, vw),
      height: Math.min(size.height, vh),
    };
  }
  if (position) {
    const w = out.size?.width ?? size?.width ?? 0;
    const h = out.size?.height ?? size?.height ?? 0;
    out.position = {
      x: Math.max(MIN_VIS - w, Math.min(position.x, vw - MIN_VIS)),
      y: Math.max(0, Math.min(position.y, vh - MIN_VIS)),
    };
  }
  return out;
};

export const loadExplorerState = (
  entityName: string,
  userId: string | null | undefined,
): SavedExplorerState | null => {
  try {
    const raw = localStorage.getItem(explorerStorageKey(entityName, userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isValidState(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const saveExplorerState = (
  entityName: string,
  userId: string | null | undefined,
  state: Omit<SavedExplorerState, 'v'>,
): void => {
  try {
    const payload: SavedExplorerState = { v: SCHEMA_VERSION, ...state };
    localStorage.setItem(explorerStorageKey(entityName, userId), JSON.stringify(payload));
  } catch {
    // localStorage may be unavailable (private mode, quota); silently skip
  }
};

export const clearExplorerState = (
  entityName: string,
  userId: string | null | undefined,
): void => {
  try {
    localStorage.removeItem(explorerStorageKey(entityName, userId));
  } catch {
    // ignore
  }
};
